import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';

export function AGB() {
  return (
    <div className="min-h-screen bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Link
          to="/login"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-8"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Zurück zur Anmeldung
        </Link>

        {/* Content */}
        <div className="bg-card shadow-sm rounded-lg border border-border p-8">
          <div className="flex items-center mb-8">
            <FileText className="h-8 w-8 text-primary mr-3" />
            <h1 className="text-3xl font-bold text-foreground">
              Allgemeine Geschäftsbedingungen (AGB)
            </h1>
          </div>

          <div className="prose prose-sm max-w-none text-foreground space-y-8">
            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">
                § 1 Geltungsbereich
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Diese Allgemeinen Geschäftsbedingungen gelten für alle Verträge zwischen AllFlowX (Ali Tombul, Karl-Joos-Straße 8, 70806 Kornwestheim) und unseren Kunden über die Nutzung unserer Software-as-a-Service (SaaS) Plattform.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">
                § 2 Vertragsgegenstand
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Gegenstand des Vertrages ist die zeitlich begrenzte Überlassung der Software zur Nutzung über das Internet sowie die Erbringung damit zusammenhängender Leistungen. AllFlowX bietet eine cloudbasierte Plattform für Aufgabenverwaltung, Temperaturkontrolle, Bestellungen, MHD-Verwaltung und weitere betriebliche Workflows.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">
                § 3 Vertragsschluss und Registrierung
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Der Vertragsschluss erfolgt durch:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Registrierung auf unserer Plattform</li>
                <li>Bestätigung der AGB und Datenschutzerklärung</li>
                <li>Auswahl eines Tarifs</li>
                <li>Abschluss des Checkout-Prozesses über Stripe</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-3">
                Mit Abschluss der Registrierung kommt ein verbindlicher Vertrag zustande.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">
                § 4 Leistungsbeschreibung und Verfügbarkeit
              </h2>
              
              <h3 className="text-lg font-medium text-foreground mb-3">4.1 Leistungsumfang</h3>
              <p className="text-muted-foreground leading-relaxed mb-3">
                AllFlowX stellt folgende Kernfunktionalitäten als SaaS-Lösung bereit:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4 mb-4">
                <li>Aufgabenverwaltung (täglich, wöchentlich, Schicht, persönlich)</li>
                <li>Temperaturprotokollierung (Kühlschrank, Tiefkühler)</li>
                <li>Bestellverwaltung und Produktkatalog</li>
                <li>MHD-Kontrolle und Abschriften</li>
                <li>Unterweisungen und Schulungsdokumentation</li>
                <li>Verbesserungsvorschläge und Benachrichtigungssystem</li>
                <li>Mehrmandantenfähigkeit (Multi-Tenant Architecture)</li>
                <li>Rollenbasierte Zugriffsverwaltung (Admin, Stationsleitung, Mitarbeiter)</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mb-3">4.2 Verfügbarkeit (SLA)</h3>
              <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-lg p-4 mb-3">
                <p className="text-foreground font-semibold mb-2">Service Level Agreement (SLA):</p>
                <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4 text-sm">
                  <li><strong className="text-foreground">Verfügbarkeit:</strong> 98% im Jahresmittel (Uptime Guarantee)</li>
                  <li><strong className="text-foreground">Messperiode:</strong> Kalenderjahr (01.01. – 31.12.)</li>
                  <li><strong className="text-foreground">Geplante Wartung:</strong> Max. 4 Stunden/Monat (außerhalb der SLA)</li>
                </ul>
              </div>

              <h3 className="text-lg font-medium text-foreground mb-3">4.3 Ausnahmen vom SLA</h3>
              <p className="text-muted-foreground leading-relaxed mb-2">
                Von der Verfügbarkeitsgarantie ausgenommen sind:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4 text-sm">
                <li>Geplante Wartungsarbeiten (werden mind. 48h im Voraus angekündigt)</li>
                <li>Technische Probleme außerhalb unseres Einflussbereichs (z.B. höhere Gewalt, DDoS-Angriffe, Verschulden Dritter)</li>
                <li>Ausfälle aufgrund von Internet-Provider-Störungen beim Kunden</li>
                <li>Störungen durch unsachgemäße Nutzung oder Missbrauch durch den Kunden</li>
              </ul>

              <h3 className="text-lg font-medium text-foreground mb-3 mt-4">4.4 Support</h3>
              <p className="text-muted-foreground leading-relaxed text-sm">
                Support-Anfragen werden per E-Mail entgegengenommen (support@allflowx.de). 
                Reaktionszeit: i.d.R. innerhalb von 24 Stunden (Werktage). Kritische Störungen 
                werden priorisiert behandelt.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">
                § 5 Preise und Zahlungsbedingungen
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Die Preise richten sich nach dem gewählten Tarif und sind auf unserer Website einsehbar. Die Abrechnung erfolgt monatlich im Voraus über den Zahlungsdienstleister Stripe. Die erste Zahlung wird fällig nach Ablauf der kostenlosen Testphase von <strong className="text-foreground">14 Tagen</strong>.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Alle Preise verstehen sich zzgl. der gesetzlichen Mehrwertsteuer. Preisänderungen bleiben vorbehalten und werden dem Kunden mindestens 30 Tage vor Inkrafttreten per E-Mail mitgeteilt.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">
                § 6 Testphase (Trial)
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Neukunden erhalten eine kostenlose Testphase von <strong className="text-foreground">14 Tagen</strong>. Nach Ablauf der Testphase wird automatisch der gewählte kostenpflichtige Tarif aktiviert, sofern nicht vorher über das Kundenportal gekündigt wurde. Während der Testphase haben Kunden vollen Zugriff auf alle gebuchten Funktionen.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">
                § 7 Kündigung und Vertragslaufzeit
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Das Abonnement kann jederzeit zum Ende des jeweiligen Abrechnungszeitraums über das Kundenportal gekündigt werden. Die Kündigung muss in Textform erfolgen (E-Mail oder über die Kündigungsfunktion im Portal).
              </p>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Nach Vertragsende werden personenbezogene Daten gemäß unserem Löschkonzept nach einer Frist von <strong className="text-foreground">30 Tagen</strong> endgültig gelöscht, sofern keine gesetzlichen Aufbewahrungspflichten entgegenstehen (z.B. Rechnungsdaten werden für 10 Jahre aufbewahrt).
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Das Recht zur außerordentlichen Kündigung aus wichtigem Grund bleibt unberührt.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">
                § 8 Pflichten des Nutzers
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Der Nutzer verpflichtet sich:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Die Software nur im Rahmen der vertraglichen Vereinbarungen und geltenden Gesetze zu nutzen</li>
                <li>Seine Zugangsdaten (E-Mail, Passwort) geheim zu halten und vor unbefugtem Zugriff zu schützen</li>
                <li>Keine rechtswidrigen Inhalte zu speichern oder zu verbreiten</li>
                <li>Regelmäßige Backups seiner Daten über die Export-Funktion durchzuführen</li>
                <li>Uns unverzüglich zu informieren, wenn er Kenntnis von einem unbefugten Zugriff auf seinen Account erhält</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">
                § 9 Haftung
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Wir haften unbeschränkt für Schäden aus der Verletzung des Lebens, des Körpers oder der Gesundheit, die auf einer vorsätzlichen oder fahrlässigen Pflichtverletzung unsererseits oder eines gesetzlichen Vertreters oder Erfüllungsgehilfen beruhen.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Für sonstige Schäden haften wir nur bei Vorsatz und grober Fahrlässigkeit. Bei leichter Fahrlässigkeit haften wir nur bei Verletzung wesentlicher Vertragspflichten (Kardinalpflichten), deren Erfüllung die ordnungsgemäße Durchführung des Vertrags überhaupt erst ermöglicht.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Die Haftung für Datenverlust ist auf den typischen Wiederherstellungsaufwand beschränkt, der bei regelmäßiger Anfertigung von Sicherungskopien eingetreten wäre.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">
                § 10 Schlussbestimmungen
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-3">
                Es gilt das Recht der Bundesrepublik Deutschland unter Ausschluss des UN-Kaufrechts.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Gerichtsstand ist <strong className="text-foreground">Ludwigsburg</strong>, soweit der Kunde Kaufmann, juristische Person des öffentlichen Rechts oder öffentlich-rechtliches Sondervermögen ist oder keinen allgemeinen Gerichtsstand in Deutschland hat.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">
                § 11 Kontakt
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Bei Fragen zu diesen AGB wenden Sie sich bitte an:
              </p>
              <div className="bg-muted rounded-lg p-4 mt-3">
                <p className="text-foreground font-semibold">AllFlowX</p>
                <p className="text-muted-foreground">Ali Tombul</p>
                <p className="text-muted-foreground">Karl-Joos-Straße 8</p>
                <p className="text-muted-foreground">70806 Kornwestheim</p>
                <p className="text-muted-foreground mt-2">E-Mail: <a href="mailto:support@allflowx.de" className="text-primary hover:underline">support@allflowx.de</a></p>
                <p className="text-muted-foreground">Telefon: +49 (0) 172 701 20 56</p>
              </div>
            </section>
          </div>

          <div className="mt-8 pt-6 border-t border-border">
            <p className="text-xs text-muted-foreground">
              Version 2.0 | Stand: {new Date().toLocaleDateString('de-DE')} | Zuletzt aktualisiert: Februar 2025
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
